import styled from "@emotion/styled";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { paper, fonts, easing, paperA } from "../theme";
import { useWebSocket } from "../useWebSocket";
import type { PlayerInfo } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsEyebrow, MsStamp,
    MsPaper,
    MsBtn, MsBackButton,
    MsRow, MsAvatar, MsName, MsNameMark, MsPlayerButton,
    Stack,
} from "../ms";

const RosterHeader = styled(MsPaper)`
    padding: 14px 18px;
`;

const RowMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
    flex-shrink: 0;
`;

const Chevron = styled.span<{ open: boolean }>`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 22px;
    line-height: 1;
    color: ${paper.inkSoft};
    transform: rotate(${p => p.open ? 90 : 0}deg);
    transition: transform 0.2s ${easing.pop};
`;

const Drawer = styled(motion.div)`
    overflow: hidden;
`;

const DrawerInner = styled.div`
    padding: 12px 6px 4px;
`;

const ActionBtn = styled(MsBtn)`
    justify-content: space-between;
    text-align: left;
    width: 100%;
`;

const ActionLabel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
`;

const ActionTitle = styled.span``;

const ActionHint = styled.span<{ light?: boolean }>`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 500;
    font-size: 12px;
    color: ${p => p.light ? paperA(0.75) : paper.inkSoft};
`;

/** "That's you" / "Host" / "Player" info note inside a drawer. */
const InfoNote = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 14px;
    line-height: 1.4;
    color: ${paper.inkSoft};
    padding: 4px 6px 6px;
`;

const EmptyBody = styled(MsBody)`
    justify-content: center;
    align-items: center;
    gap: 14px;
`;

const RosterBody = styled(MsBody)`
    gap: 14px;
    padding-bottom: 22px;
`;

const RosterEyebrow = styled(MsEyebrow)`
    margin-bottom: 4px;
`;

const RosterDescription = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 14px;
    color: ${paper.inkSoft};
`;

const RosterName = styled(MsName)`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActionGlyphRed = styled.span`
    color: ${paper.red};
    font-size: 22px;
    font-family: ${fonts.serif};
    font-weight: 900;
`;

const ActionGlyph = styled.span`
    font-family: ${fonts.serif};
    font-weight: 900;
    font-size: 22px;
`;

interface Props {
    players: PlayerInfo[];
    selfId: string;
    isHost: boolean;
    onClose: () => void;
    /** Open the file directly to this player. Falls back to the first player. */
    initialPlayerId?: string;
}

type ActionTaken = { playerId: string; kind: "kicked" | "host" };

/**
 * Roster sheet — one row per player, tap to expand inline actions/info.
 * Host actions surface in the drawer of the selected player; non-actionable
 * relations (self, other host, other player) show a short context note.
 */
export default function PaperPlayerManage({ players, selfId, isHost, onClose, initialPlayerId }: Props) {
    const { sendMessage } = useWebSocket();
    const [expandedId, setExpandedId] = useState<string | null>(
        initialPlayerId && players.some(p => p.id === initialPlayerId)
            ? initialPlayerId
            : null
    );
    const [actionTaken, setActionTaken] = useState<ActionTaken | null>(null);

    if (players.length === 0) {
        return (
            <MsScreen>
                <EmptyBody>
                    <MsEyebrow>Empty roster</MsEyebrow>
                </EmptyBody>
                <MsFooter><MsBtn variant="ink" onClick={onClose}>Back</MsBtn></MsFooter>
            </MsScreen>
        );
    }

    const handleKick = (p: PlayerInfo) => {
        sendMessage({ type: "kick_player", playerId: p.id });
        setActionTaken({ playerId: p.id, kind: "kicked" });
    };
    const handleTransfer = (p: PlayerInfo) => {
        sendMessage({ type: "transfer_host", playerId: p.id });
        setActionTaken({ playerId: p.id, kind: "host" });
    };

    return (
        <MsScreen>
            <RosterBody>
                <MsBackButton onClick={onClose} label="Roster" />

                <RosterHeader deckle>
                    <RosterEyebrow tone="red">Roster</RosterEyebrow>
                    <RosterDescription>
                        {players.length} {players.length === 1 ? "player" : "players"} in the room.
                        {isHost && " Tap a name to manage."}
                    </RosterDescription>
                </RosterHeader>

                <Stack gap={8}>
                    {players.map(p => {
                        const open = p.id === expandedId;
                        const isSelf = p.id === selfId;
                        const isPHost = !!p.isHost;
                        const isConnected = p.isConnected ?? true;
                        const initials = p.name.slice(0, 2);
                        const pRole = isPHost ? "Host" : isSelf ? "You" : "Player";
                        const canActOn = isHost && !isSelf && !isPHost;
                        const stamp = actionTaken?.playerId === p.id ? actionTaken.kind : null;

                        const metaText = stamp
                            ? null
                            : !isConnected
                                ? `${pRole} · offline`
                                : pRole;

                        return (
                            <div key={p.id}>
                                <MsPlayerButton
                                    onClick={() => setExpandedId(open ? null : p.id)}
                                    aria-expanded={open}
                                >
                                    <MsRow you={isSelf}>
                                        <MsAvatar you={isSelf}>{initials}</MsAvatar>
                                        <RosterName>
                                            {isPHost && <MsNameMark you={isSelf}>★</MsNameMark>}
                                            {p.name}
                                        </RosterName>
                                        <RowMeta>
                                            {stamp && (
                                                <MsStamp size="sm" tone={stamp === "kicked" ? "red" : "ink"}>
                                                    {stamp === "kicked" ? "Removed" : "Hands on"}
                                                </MsStamp>
                                            )}
                                            {metaText && (
                                                <MsEyebrow tone={
                                                    !isConnected ? "red" : isSelf ? "sticky" : "mute"
                                                }>
                                                    {metaText}
                                                </MsEyebrow>
                                            )}
                                            <Chevron open={open} aria-hidden>›</Chevron>
                                        </RowMeta>
                                    </MsRow>
                                </MsPlayerButton>

                                <AnimatePresence initial={false}>
                                    {open && (
                                        <Drawer
                                            key="drawer"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                                        >
                                            <DrawerInner>
                                                {canActOn && stamp === null && (
                                                    <Stack gap={8}>
                                                        <ActionBtn onClick={() => handleTransfer(p)}>
                                                            <ActionLabel>
                                                                <ActionTitle>Hand over the gavel</ActionTitle>
                                                                <ActionHint>Make {p.name} the host</ActionHint>
                                                            </ActionLabel>
                                                            <ActionGlyphRed>★</ActionGlyphRed>
                                                        </ActionBtn>
                                                        <ActionBtn variant="red" haptic="destructive" onClick={() => handleKick(p)}>
                                                            <ActionLabel>
                                                                <ActionTitle>Show {p.name} the door</ActionTitle>
                                                                <ActionHint light>Removes them from the room</ActionHint>
                                                            </ActionLabel>
                                                            <ActionGlyph>×</ActionGlyph>
                                                        </ActionBtn>
                                                    </Stack>
                                                )}

                                                {isSelf && (
                                                    <InfoNote>
                                                        That's you. Pick a new name on the entry screen, or leave the room from the host menu.
                                                    </InfoNote>
                                                )}

                                                {isPHost && !isSelf && (
                                                    <InfoNote>
                                                        {p.name} runs this room. Only they can change the rules.
                                                    </InfoNote>
                                                )}

                                                {!isHost && !isSelf && !isPHost && (
                                                    <InfoNote>
                                                        Only the host can manage other players.
                                                    </InfoNote>
                                                )}
                                            </DrawerInner>
                                        </Drawer>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </Stack>
            </RosterBody>
            <MsFooter>
                <MsBtn variant="ink" onClick={onClose}>Done</MsBtn>
            </MsFooter>
        </MsScreen>
    );
}
